
import React from "react";
import { Menu, Bell, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { GlobalSearch } from "@/components/search/GlobalSearch";
import { MobileNav } from "./MobileNav";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMenuAccessibility, generateAriaLabel } from "@/utils/accessibility"; 

interface HeaderProps {
  toggleSidebar: () => void;
  sidebarOpen: boolean;
  onAnnounce?: (message: string) => void;
}

export function Header({ toggleSidebar, sidebarOpen, onAnnounce }: HeaderProps) {
  const { user, signOut } = useAuth();
  const isMobile = useIsMobile();

  // Accessibility hooks for dropdowns
  const notificationsMenu = useMenuAccessibility();
  const userMenu = useMenuAccessibility();

  const userInitials = user?.email
    ? user.email.substring(0, 2).toUpperCase()
    : "??";

  const handleSignOut = async () => {
    await signOut();
    onAnnounce?.("Signed out successfully");
  };

  const handleNotificationToggle = () => {
    notificationsMenu.setIsOpen(!notificationsMenu.isOpen);
    if (!notificationsMenu.isOpen) {
      onAnnounce?.("Notifications menu opened");
    }
  };

  const handleUserMenuToggle = () => {
    userMenu.setIsOpen(!userMenu.isOpen);
    if (!userMenu.isOpen) {
      onAnnounce?.("User menu opened");
    }
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6">
      {/* Mobile Navigation */}
      {isMobile ? (
        <MobileNav />
      ) : (
        <Button
          variant="ghost"
          size="icon"
          className="hidden md:inline-flex"
          onClick={toggleSidebar}
          aria-label={sidebarOpen ? "Collapse navigation menu" : "Expand navigation menu"}
          aria-expanded={sidebarOpen}
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle Sidebar</span>
        </Button>
      )}

      <div className="ml-auto flex items-center gap-2">
        <div className="hidden md:flex">
          <GlobalSearch />
        </div>
        
        <DropdownMenu open={notificationsMenu.isOpen} onOpenChange={notificationsMenu.setIsOpen}>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleNotificationToggle}
              aria-label={generateAriaLabel({
                type: "Notifications button",
                status: notificationsMenu.isOpen ? "expanded" : "collapsed",
                count: 0
              })}
              {...notificationsMenu.triggerProps}
            >
              <Bell className="h-5 w-5" />
              <span className="sr-only">Open notifications menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="end"
            {...notificationsMenu.menuProps}
            onCloseAutoFocus={(e) => {
              // Return focus to trigger button
              e.preventDefault();
              const trigger = document.getElementById(notificationsMenu.triggerProps.id);
              trigger?.focus();
            }}
          >
            <DropdownMenuLabel>Notifications</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              {...notificationsMenu.getMenuItemProps()}
              disabled
            >
              No new notifications
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu open={userMenu.isOpen} onOpenChange={userMenu.setIsOpen}>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-full"
              onClick={handleUserMenuToggle}
              aria-label={generateAriaLabel({
                type: "User menu button",
                name: user?.email || "User",
                status: userMenu.isOpen ? "expanded" : "collapsed"
              })}
              {...userMenu.triggerProps}
            >
              <Avatar className="h-8 w-8">
                <AvatarImage 
                  src="" 
                  alt={`Avatar for ${user?.email || 'User'}`} 
                  role="img"
                />
                <AvatarFallback 
                  aria-label={`User initials: ${userInitials}`}
                >
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <span className="sr-only">Open user menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="end"
            {...userMenu.menuProps}
            onCloseAutoFocus={(e) => {
              // Return focus to trigger button
              e.preventDefault();
              const trigger = document.getElementById(userMenu.triggerProps.id);
              trigger?.focus();
            }}
          >
            <DropdownMenuLabel>{user?.email || 'My Account'}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              {...userMenu.getMenuItemProps(() => {
                // Navigate to profile - would implement routing
                onAnnounce?.("Navigating to profile");
              })}
            >
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem 
              {...userMenu.getMenuItemProps(() => {
                // Navigate to settings - would implement routing
                onAnnounce?.("Navigating to settings");
              })}
            >
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              {...userMenu.getMenuItemProps(handleSignOut)}
              className="text-red-600 focus:text-red-600 focus:bg-red-50"
            >
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
